import {
  buildMaterialsFolderTree,
  type MaterialsFolderTreeNode,
} from "@/lib/materials-folder-tree";
import { brandSiteAssetReferenceContext } from "@/lib/brand-site-api";
import type {
  MaterialsFolder,
  MaterialsItem,
  MaterialsListContext,
  MaterialsListOptions,
} from "@/lib/materials-api";

export type PickerVault = "enterprise" | "brand" | "site";

export type PickerNav = {
  vault: PickerVault;
  /** null 表示该素材仓的工作根。 */
  folderId: number | null;
};

export const PICKER_VAULT_LABEL: Record<PickerVault, string> = {
  enterprise: "账号素材",
  brand: "品牌素材",
  site: "本站素材",
};

export type PickerListRow = {
  vault: PickerVault;
  key: string;
  item: MaterialsItem;
};

export type PickerWorkRootIds = Record<PickerVault, number | null>;

type PickerVaultInput = {
  folders: MaterialsFolder[];
  workRootId: number | null;
};

/** 各仓侧栏只展示本仓职责目录，避免与「品牌素材 / 本站素材」重复。 */
export function foldersForPickerVault(
  vault: PickerVault,
  folders: MaterialsFolder[],
): MaterialsFolder[] {
  if (vault === "enterprise") {
    // 账号仓：排除品牌系统根（及其子树不会被挂上）
    return folders.filter((folder) => folder.binding_kind !== "brand_root");
  }
  if (vault === "brand") {
    // 品牌仓：排除站点系统根（站点入口在「本站素材」）
    return folders.filter((folder) => folder.binding_kind !== "brand_site_root");
  }
  return folders;
}

export function buildPickerVaultForest(input: {
  enterprise: PickerVaultInput;
  brand: PickerVaultInput;
  site: PickerVaultInput;
}): Array<{
  vault: PickerVault;
  label: string;
  workRootId: number | null;
  children: MaterialsFolderTreeNode[];
}> {
  const vaults: PickerVault[] = ["enterprise", "brand", "site"];

  return vaults.map((vault) => {
    const folders = foldersForPickerVault(vault, input[vault].folders);
    return {
      vault,
      label: PICKER_VAULT_LABEL[vault],
      workRootId: input[vault].workRootId,
      children: buildMaterialsFolderTree(folders, input[vault].workRootId),
    };
  });
}

/** 按文件名、素材编号或备注搜索当前列表。 */
export function filterPickerItems(items: MaterialsItem[], query: string): MaterialsItem[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return items;

  return items.filter((item) => {
    const notes = item.notes?.toLowerCase() ?? "";
    return (
      item.name.toLowerCase().includes(normalizedQuery) ||
      item.slug.toLowerCase().includes(normalizedQuery) ||
      notes.includes(normalizedQuery)
    );
  });
}

export function pickerListContext(
  vault: PickerVault,
  brandId: string | number,
  brandSiteId: string | number,
): MaterialsListContext {
  if (vault === "enterprise") return { context: "enterprise" };
  if (vault === "brand") return { context: "brand", brandId };
  return { context: "brand_site", brandId, brandSiteId };
}

export function pickerUploadFolderId(
  nav: PickerNav,
  workRootIds: PickerWorkRootIds,
): number | null {
  return nav.folderId ?? workRootIds[nav.vault];
}

export function pickerItemsQuery(
  nav: PickerNav,
  workRootIds: PickerWorkRootIds,
  query: string,
  brandId: string | number,
  brandSiteId: string | number,
): MaterialsListOptions {
  const searching = query.trim().length > 0;
  return {
    ...pickerListContext(nav.vault, brandId, brandSiteId),
    folderId: searching ? workRootIds[nav.vault] : pickerUploadFolderId(nav, workRootIds),
    subtree: searching,
  };
}

export function buildPickerListRows(
  vault: PickerVault,
  items: MaterialsItem[],
): PickerListRow[] {
  return items.map((item) => ({
    vault,
    key: `${vault}:${item.id}`,
    item,
  }));
}

export function buildPickerReferencePayload(item: MaterialsItem, pageSlug?: string | null) {
  return {
    material_id: item.id,
    context: brandSiteAssetReferenceContext(pageSlug),
    filename: item.name,
    content_type: item.content_type,
    byte_size: item.byte_size,
  };
}
